export const getProxiedUrl = (url: string | undefined | null): string => {
    if (!url || typeof url !== 'string') return ''
    if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
    return url
}

/**
 * For MusicLabel logos. Previously assumed every label logo lived on a
 * public-read bucket and could be rendered directly — that's true for new
 * uploads to the platform Hetzner bucket, but NOT for legacy Psyfeature
 * logos which sit on a private Wasabi bucket. Routing through
 * `/api/assets/serve` lets the server sign the request with the tenant's
 * S3 credentials so both shapes work, and strips any stale presigned
 * query params as a bonus.
 */
export const getLogoUrl = (url: string | undefined | null): string => {
    if (!url || typeof url !== 'string') return ''
    const clean = url.split('?')[0]
    if (clean.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(clean)}`
    return clean
}

/**
 * Absolute, crawler-fetchable URL for an og:image / twitter:image.
 *
 * A share image is not an `<img src>` and fails in two ways an in-page image
 * never does.
 *
 * It has to be ABSOLUTE. Facebook resolves a relative og:image against the
 * page it found it on; most other consumers do not, and the spec says the
 * value is a URL.
 *
 * And it is fetched by an ANONYMOUS crawler, so it cannot point straight at
 * object storage unless that storage is public. Psyfeature's Wasabi account
 * refuses public reads at the account level — "Public use of objects is not
 * allowed by this account" — so the raw S3 URL the admin panel stores answered
 * 403 with an XML body to every scrape. Facebook reported that as "could not
 * be processed as an image. Possibly the content type is not valid", which is
 * its wording for "what came back was not a picture".
 *
 * So anything not already on our own origin goes through /api/assets/serve,
 * which signs the request with the tenant's credentials. `inline=1` because a
 * consumer that keeps the RESOLVED target of the usual 302 would be holding a
 * presigned URL good for one hour.
 */
export const getShareImageUrl = (url: string | undefined | null, origin: string, opts: { width?: number } = {}): string => {
    if (!url || typeof url !== 'string') return ''
    const base = String(origin || '').replace(/\/+$/, '')
    // A data: URI is a legitimate value elsewhere in the app (merch designs
    // render one as a last resort) but never a share image — a crawler cannot
    // fetch it, and prefixing it with the origin would emit nonsense.
    if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:\/\//i.test(url)) return ''
    // Relative already means "on this site" — it only needs the origin.
    if (!/^https?:\/\//i.test(url)) return `${base}/${url.replace(/^\/+/, '')}`
    // Ours already. Proxying it would make the server fetch itself.
    if (base && url.startsWith(`${base}/`)) return url
    // Drop any stale presigned query — the proxy signs its own request.
    // `width` (1200) makes the proxy scale the image down to a JPEG: stored
    // originals are masters of several MB, over X's 5 MB share limit.
    const size = opts.width ? `&w=${opts.width}` : ''
    return `${base}/api/assets/serve?url=${encodeURIComponent(url.split('?')[0]!)}&inline=1${size}`
}
